import React, { useEffect, useState } from 'react';
import { Button, Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import { useMount } from 'react-use';
import { debounce } from 'lodash-es';
import useWindow from '../../Hooks/useWindow';

export interface Props {
  items: Map<string | JSX.Element, JSX.Element>;
  tabIdentifier: string;
  activeTab?: number;
  onTabClick?: (tabId: number) => void;
  className?: string;
  trailing?: JSX.Element;
  withSeparators?: boolean;
}

function persistTabMap(
  tabMapStorageKey: string,
  mapOfTabs: Map<string, number>,
  window: Window | null | undefined
): void {
  window?.localStorage?.setItem(tabMapStorageKey, JSON.stringify([...mapOfTabs]));
}

const InlineTabs = (props: Props): JSX.Element => {
  const window = useWindow();
  const tabMapStorageKey = 'TAB_MAP';
  const [activeTab, setActiveTab] = useState(0);
  const tabMapJson = window?.localStorage?.getItem(tabMapStorageKey);
  const tabMap = tabMapJson
    ? new Map<string, number>(JSON.parse(tabMapJson))
    : new Map<string, number>();
  const [isOverflowing, setOverflowing] = useState<boolean>(false);
  const [hasScrollLeft, setScrollLeft] = useState<boolean>(false);
  const [hasScrollRight, setScrollRight] = useState<boolean>(false);
  const ref = React.useRef<HTMLLIElement>(null);

  useMount(() => {
    if (ref && ref.current) {
      setOverflowing(ref.current.clientWidth < ref.current.scrollWidth);
      handleScrollVisibility(ref.current);
    }
    if (tabMap.has(props.tabIdentifier)) {
      const startingActiveTab = props.activeTab || tabMap.get(props.tabIdentifier);
      setActiveTab(startingActiveTab || 0);
    } else {
      tabMap.set(props.tabIdentifier, props.activeTab || 0);
      persistTabMap(tabMapStorageKey, tabMap, window);
    }
  });

  useEffect(() => {
    setActiveTab(props.activeTab || 0);
  }, [props.activeTab]);

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      if (ref && ref.current) {
        if (ref.current.clientWidth < ref.current.scrollWidth) {
          setOverflowing(true);
          handleScrollVisibility(ref.current);
        } else {
          setOverflowing(false);
        }
      }
    }, 300);

    window?.addEventListener('resize', debouncedHandleResize);
    return (): void => {
      window?.removeEventListener('resize', debouncedHandleResize);
    };
  }, [ref]);

  const { items, tabIdentifier, className, trailing, withSeparators } = props;

  const labelsToRender = [...items.keys()];
  const componentsToRender = [...items.values()];

  const handleScrollVisibility = (currentRef: {
    scrollLeft: number;
    scrollWidth: number;
    clientWidth: number;
  }): void => {
    setScrollLeft(currentRef.scrollLeft > 0);
    setScrollRight(currentRef.scrollWidth - currentRef.clientWidth !== currentRef.scrollLeft);
  };

  const slide = (direction: string): void => {
    if (ref && ref.current) {
      if (direction === 'left') {
        ref.current.scrollLeft += 100;
      } else {
        ref.current.scrollLeft -= 100;
      }
      handleScrollVisibility(ref.current);
    }
  };

  function renderTab(label: string | JSX.Element, id: number): JSX.Element {
    return (
      <NavItem className={activeTab === id ? 'active' : ''} key={id}>
        <NavLink
          className={activeTab === id ? 'active' : ''}
          onClick={(): void => {
            tabMap.set(tabIdentifier, id);
            persistTabMap(tabMapStorageKey, tabMap, window);
            setActiveTab(id);
            if (props.onTabClick) {
              props.onTabClick(id);
            }
          }}
          href={'#'}
        >
          {label}
        </NavLink>
        {withSeparators && <div className="iso__inlineTabs-separator" />}
      </NavItem>
    );
  }

  function renderTabContent(component: JSX.Element, id: number): JSX.Element {
    return (
      <TabPane tabId={id} key={id}>
        <Row>
          <Col sm="12" className="pt-1">
            {component}
          </Col>
        </Row>
      </TabPane>
    );
  }

  return (
    <div>
      <Row className="flex-md-nowrap w-100 nav-tabs" noGutters>
        <Nav className={'iso__inlineTabs-nav position-relative ' + className ?? ''}>
          <li className="d-flex flex-nowrap w-100 overflow-hidden" ref={ref}>
            {labelsToRender.map((label, index) => {
              return renderTab(label, index);
            })}
            {isOverflowing && (
              <>
                {hasScrollLeft && (
                  <Button
                    color="outline"
                    className="iso__inlineTabs_overflowingButton_left"
                    onClick={(): void => slide('right')}
                  />
                )}
                {hasScrollRight && (
                  <Button
                    color="outline"
                    className="iso__inlineTabs_overflowingButton_right"
                    onClick={(): void => slide('left')}
                  />
                )}
              </>
            )}
          </li>
        </Nav>
        {trailing}
      </Row>
      <TabContent activeTab={activeTab}>
        {componentsToRender.map((component, index) => {
          return renderTabContent(component, index);
        })}
      </TabContent>
    </div>
  );
};

export default InlineTabs;
